import React from 'react';
import styled from '@emotion/styled';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import {
  DeprecatedButtonGroup as ButtonGroup,
  BodyTextMedium,
  HeadingText50,
} from '@modernhealth/vital-components';
import { captureException as sentryCaptureException } from '@sentry/react';
import { AnalyticsEventType, useAnalytics } from '@modernhealth/web-analytics';

import { captureException } from 'src/shared/utils/captureException';

import { useCancelUnauthenticatedRsvpMutation } from '../documents/rsvp.generated';
import { ReactComponent as Cancelation } from '../assets/images/Cancelation.svg';
import TopicPageSkeleton from './skeletons/TopicPageSkeleton';

interface RsvpParams {
  rsvpId: string;
}

const PageWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  maxWidth: '32rem',
  margin: '0 auto',
  alignItems: 'center',
  gap: '16px',
});

const RsvpCancellation: React.FC<unknown> = () => {
  const analytics = useAnalytics();
  const history = useHistory();
  const location = useLocation();
  const { rsvpId } = useParams<RsvpParams>();
  const { token } = queryString.parse(location.search);

  const [cancelUnauthenticatedRsvp, { loading }] = useCancelUnauthenticatedRsvpMutation();

  React.useEffect(() => {
    if (!token || typeof token !== 'string') {
      history.push('/bad-token');
      return;
    }

    cancelUnauthenticatedRsvp({
      variables: {
        rsvpId,
        token,
      },
    })
      .then(() => {
        analytics.track('Circles: Cancel RSVP', AnalyticsEventType.API);
      })
      .catch(e => {
        sentryCaptureException(e);
        captureException(e);
      });
  }, [cancelUnauthenticatedRsvp, token, analytics, history, rsvpId]);

  //TODO: Cancelation Skeleton- Using TopicPageSkeleton for now
  if (loading) {
    return <TopicPageSkeleton />;
  }

  return (
    <PageWrapper>
      <Cancelation height="15rem" width="15rem" css={{ marginBottom: '3rem' }} />
      <HeadingText50>Cancellation Successful</HeadingText50>
      <BodyTextMedium centered fontColor="gray55">
        Thank you for letting us know! You are no longer registered for this Circle. You can explore
        other sessions for this Circle, or check out other topics.
      </BodyTextMedium>
      <ButtonGroup
        firstButtonText="View other series"
        onFirstButtonClick={() => {
          history.push('/');
        }}
        secondButtonText="View upcoming Circles"
        onSecondButtonClick={() => {
          history.push('/upcoming');
        }}
      />
    </PageWrapper>
  );
};

export default RsvpCancellation;
